/**
 * Parsing of the name-locate answer in anonymize-submission-pages.ts (phase A): every place on a page that identifies
 * the student, as boxes to black out. Pure, so it is unit-tested on its own (tests/server/name-boxes.test.ts).
 */

/** 0-1000 normalized page coordinates, origin top-left. */
export type Box = { x1: number; y1: number; x2: number; y2: number };

export const NAME_BOX_KINDS = ['name', 'signature', 'mention'] as const;
export type NameBoxKind = (typeof NAME_BOX_KINDS)[number];

/**
 * A name field, a signature or a mention is a few words on a page. A box larger than this is not a name but a
 * misreading, and blacking it out would hide answers the grading needs: the whole answer counts as a failed attempt
 * (retried, and the job fails once the retries are used up) — never shipped with the box dropped.
 */
export const MAX_NAME_BOX_AREA_FRACTION = 0.15;

export interface LocatedNames {
  /** Everything to black out. Empty when the page shows nothing that identifies the student. */
  boxes: Box[];
  /** The one name field read in phase B. Null when the page has only signatures or mentions, or nothing at all. */
  primary: Box | null;
}

/** Accepts either the object shape the prompt asks for (`{x1,y1,x2,y2}`) or a plain `[x1,y1,x2,y2]` array —
 * qwen has been observed returning the array form despite the prompt's own JSON example, and that's still a
 * perfectly valid, usable box; rejecting it and burning a retry (then eventually throwing) over a shape
 * mismatch would be treating a parsing gap as if it were missing data. */
export function parseBox(raw: unknown): Box | null {
  const b: any = raw;
  const [x1, y1, x2, y2] = Array.isArray(b)
    ? [Number(b[0]), Number(b[1]), Number(b[2]), Number(b[3])]
    : [Number(b?.x1), Number(b?.y1), Number(b?.x2), Number(b?.y2)];
  if (![x1, y1, x2, y2].every(Number.isFinite) || x2 <= x1 || y2 <= y1) return null;
  return { x1, y1, x2, y2 };
}

/**
 * Throws on anything that is not a clean list of usable boxes: no `boxes` array, a box that cannot be parsed, or an
 * oversized one. A box is never just dropped — it might have been a name — so the caller retries instead. An unknown
 * `kind` is kept as a mention: the kind only decides which box is read, every box is blackened.
 *
 * The primary box is the `name` box the model marked as primary, else the most confident `name` box. Error messages
 * never carry the model's answer: it could contain a name despite the prompt.
 */
export function parseNameBoxes(parsed: unknown): LocatedNames {
  const entries = (parsed as { boxes?: unknown } | null)?.boxes;
  if (!Array.isArray(entries)) throw new Error('answer has no "boxes" list');

  const located = entries.map((entry: any, index) => {
    const box = parseBox(entry?.bbox);
    if (!box) throw new Error(`box ${index} is missing or invalid`);
    const area = ((box.x2 - box.x1) * (box.y2 - box.y1)) / (1000 * 1000);
    if (area > MAX_NAME_BOX_AREA_FRACTION) {
      throw new Error(`box ${index} covers ${Math.round(area * 100)}% of the page, too large for a name`);
    }
    const kind: NameBoxKind = NAME_BOX_KINDS.includes(entry?.kind) ? entry.kind : 'mention';
    const confidence = Number.isFinite(Number(entry?.confidence)) ? Number(entry.confidence) : 0;
    return { box, kind, primary: entry?.primary === true, confidence };
  });

  const names = located.filter(entry => entry.kind === 'name');
  const byConfidence = (a: { confidence: number }, b: { confidence: number }) => b.confidence - a.confidence;
  const primary = [...names.filter(entry => entry.primary)].sort(byConfidence)[0] ?? [...names].sort(byConfidence)[0];

  return { boxes: located.map(entry => entry.box), primary: primary?.box ?? null };
}
